import { useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  LayoutDashboard, UserPlus, Handshake, Wrench, MoreHorizontal, X,
  FileSpreadsheet, Database, MapPin, Settings, Download, User, ClipboardList, Columns4, Activity, Wallet, Receipt, Banknote, TrendingUp, ClipboardCheck
} from 'lucide-react'
import { usePwaInstall } from '@/hooks/usePwaInstall'
import { useMyPermissions } from '@/hooks/useRolePermissions'
import type { PermissionKey } from '@/lib/api'

interface NavItem {
  path: string
  label: string
  icon: any
  roles: string[]
  permKey?: PermissionKey
}

const ALL_ROLES = ['super_admin', 'admin', 'director', 'sales_manager', 'asst_sales_manager', 'sales_executive', 'chief_engineer', 'operation_manager', 'engineer', 'technician', 'finance', 'procurement', 'storekeeper', 'hr']
const PROJECT_BANK_ROLES = ALL_ROLES.filter(r => r !== 'hr')
const CRM_ACCESS_ROLES = ['super_admin', 'admin', 'director', 'sales_manager', 'asst_sales_manager', 'sales_executive', 'finance']
const EXCEL_IMPORT_ROLES = ['super_admin', 'admin', 'director', 'sales_manager', 'asst_sales_manager', 'sales_executive']

const PRIMARY_NAV: NavItem[] = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, roles: ALL_ROLES },
  { path: '/contacts', label: 'Contacts', icon: UserPlus, roles: CRM_ACCESS_ROLES, permKey: 'crm_view' },
  { path: '/deals', label: 'Deals', icon: Handshake, roles: CRM_ACCESS_ROLES, permKey: 'crm_view' },
  { path: '/service-requests', label: 'Service', icon: Wrench, roles: ALL_ROLES },
]

const MORE_NAV: NavItem[] = [
  { path: '/import', label: 'Excel Importer', icon: FileSpreadsheet, roles: EXCEL_IMPORT_ROLES, permKey: 'project_bank_create' },
  { path: '/customers', label: 'Project Bank', icon: Database, roles: PROJECT_BANK_ROLES },
  { path: '/site-surveys', label: 'Site Surveys', icon: MapPin, roles: ALL_ROLES },
  { path: '/handover-forms', label: 'Handovers', icon: ClipboardCheck, roles: ALL_ROLES.filter(r => r !== 'hr'), permKey: 'handover_write' },
  { path: '/budget-requests', label: 'Budget Requests', icon: Wallet, roles: ALL_ROLES },
  { path: '/expense-claims', label: 'Expense Claims', icon: Receipt, roles: ALL_ROLES },
  { path: '/advance-cash', label: 'Advance Cash', icon: Banknote, roles: ALL_ROLES },
  { path: '/analytics', label: 'Analytics', icon: TrendingUp, roles: ALL_ROLES },
  { path: '/weekly-report', label: 'Weekly Report', icon: ClipboardList, roles: ALL_ROLES },
  { path: '/users', label: 'Users', icon: Settings, roles: ['super_admin', 'admin'] },
  { path: '/activity-logs', label: 'Activity Logs', icon: Activity, roles: ['super_admin', 'admin', 'director'], permKey: 'activity_logs_view' },
  { path: '/profile', label: 'My Profile', icon: User, roles: ALL_ROLES },
]

interface Props {
  userRole: string
  pmEnabled?: boolean
}

export default function BottomNav({ userRole, pmEnabled = false }: Props) {
  const navigate = useNavigate()
  const location = useLocation()
  const [showMore, setShowMore] = useState(false)
  const { canInstall, promptInstall, isIos, isStandalone } = usePwaInstall()
  const { canDo, isSuccess: permsLoaded } = useMyPermissions()

  const filterItem = (item: NavItem) => {
    if (item.permKey) {
      if (!permsLoaded) return false
      return canDo(item.permKey)
    }
    return item.roles.includes(userRole)
  }

  const pmItem: NavItem = { path: '/project-management', label: 'Project Mgmt', icon: Columns4, roles: ALL_ROLES }
  const effectiveMoreNav = pmEnabled
    ? [MORE_NAV[0], MORE_NAV[1], MORE_NAV[2], pmItem, ...MORE_NAV.slice(3)]
    : MORE_NAV
  const primaryItems = PRIMARY_NAV.filter(filterItem)
  const moreItems = effectiveMoreNav.filter(filterItem)

  const isActive = (path: string) => location.pathname === path
  const isMoreActive = moreItems.some(item => isActive(item.path))

  return (
    <>
      {showMore && (
        <>
          <div className="fixed inset-0 bg-black/40 z-40 lg:hidden" onClick={() => setShowMore(false)} />
          <div className="fixed bottom-0 left-0 right-0 z-50 lg:hidden">
            <div className="bg-white rounded-t-2xl shadow-2xl border-t border-slate-200 pb-safe">
              <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
                <span className="text-sm font-semibold text-slate-900">More</span>
                <button onClick={() => setShowMore(false)} className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center text-slate-400">
                  <X size={20} />
                </button>
              </div>
              <div className="grid grid-cols-3 gap-1 p-3">
                {moreItems.map(item => (
                  <button
                    key={item.path}
                    onClick={() => { navigate(item.path); setShowMore(false) }}
                    className={`flex flex-col items-center gap-1.5 py-3 px-2 rounded-xl min-h-[72px] ${
                      isActive(item.path) ? 'bg-red-50 text-red-800' : 'text-slate-600 active:bg-slate-50'
                    }`}
                  >
                    <item.icon size={22} />
                    <span className="text-[11px] font-medium text-center leading-tight">{item.label}</span>
                  </button>
                ))}
                {!isStandalone && (canInstall || isIos) && (
                  <button
                    onClick={() => {
                      setShowMore(false)
                      if (canInstall) { promptInstall() }
                      else if (isIos) { alert('To install: tap the Share button in Safari, then tap "Add to Home Screen".') }
                    }}
                    className="flex flex-col items-center gap-1.5 py-3 px-2 rounded-xl min-h-[72px] text-slate-600 active:bg-slate-50"
                  >
                    <Download size={22} />
                    <span className="text-[11px] font-medium text-center leading-tight">Install App</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      <nav data-nav className="fixed bottom-0 left-0 right-0 z-30 lg:hidden bg-white border-t border-slate-200 pb-safe">
        <div className="flex items-stretch justify-around px-1">
          {primaryItems.map(item => (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className={`flex flex-col items-center justify-center gap-0.5 py-2 px-1 flex-1 min-h-[56px] ${
                isActive(item.path) ? 'text-red-800' : 'text-slate-400'
              }`}
            >
              <item.icon size={22} strokeWidth={isActive(item.path) ? 2.5 : 1.8} />
              <span className={`text-[10px] leading-tight ${isActive(item.path) ? 'font-semibold' : 'font-medium'}`}>
                {item.label}
              </span>
            </button>
          ))}
          {moreItems.length > 0 && (
            <button
              onClick={() => setShowMore(!showMore)}
              className={`flex flex-col items-center justify-center gap-0.5 py-2 px-1 flex-1 min-h-[56px] ${
                isMoreActive || showMore ? 'text-red-800' : 'text-slate-400'
              }`}
            >
              <MoreHorizontal size={22} strokeWidth={(isMoreActive || showMore) ? 2.5 : 1.8} />
              <span className={`text-[10px] leading-tight ${(isMoreActive || showMore) ? 'font-semibold' : 'font-medium'}`}>
                More
              </span>
            </button>
          )}
        </div>
      </nav>
    </>
  )
}
